import { fabric } from 'fabric';

const clamp = (v, min = 0, max = 1) => Math.max(min, Math.min(max, v));

const normalizeColor = (color) => {
  if (!color) return { hex: '#000000', alpha: 1 };
  if (typeof color === 'string') {
    if (color.startsWith('#')) {
      let hex = color.toUpperCase();
      if (hex.length === 4) {
        hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
      }
      if (hex.length > 7) hex = hex.slice(0, 7);
      return { hex, alpha: 1 };
    }
    const m1 = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/i);
    if (m1) {
      const r = parseInt(m1[1]);
      const g = parseInt(m1[2]);
      const b = parseInt(m1[3]);
      const hex =
        '#' +
        ((1 << 24) + (r << 16) + (g << 8) + b)
          .toString(16)
          .slice(1)
          .toUpperCase();
      return { hex, alpha: 1 };
    }
    const m2 = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([0-9.]+)\)/i);
    if (m2) {
      const r = parseInt(m2[1]);
      const g = parseInt(m2[2]);
      const b = parseInt(m2[3]);
      const a = parseFloat(m2[4]);
      const hex =
        '#' +
        ((1 << 24) + (r << 16) + (g << 8) + b)
          .toString(16)
          .slice(1)
          .toUpperCase();
      return { hex, alpha: a };
    }
  }
  return { hex: color.toString().toUpperCase(), alpha: 1 };
};

const traverse = (obj, cb) => {
  cb(obj);
  if (obj.getObjects && typeof obj.getObjects === 'function') {
    obj.getObjects().forEach((child) => traverse(child, cb));
  }
};

const gradientSignature = (grad) => {
  if (!grad || !grad.colorStops) return null;
  const type = grad.type || 'linear';
  const coords = grad.coords || {};
  const stops = grad.colorStops.map((cs) => {
    const { hex } = normalizeColor(cs.color);
    return hex;
  });
  return {
    type,
    stops,
    // keep coords but avoid floating-point churn by rounding
    coords: Object.fromEntries(
      Object.entries(coords).map(([k, v]) => [
        k,
        typeof v === 'number' ? Math.round(v * 1000) / 1000 : v,
      ])
    ),
  };
};

const signaturesEqual = (a, b) => {
  if (!a || !b) return false;
  if (a.type !== b.type) return false;
  if (!Array.isArray(a.stops) || !Array.isArray(b.stops)) return false;
  if (a.stops.length !== b.stops.length) return false;
  for (let i = 0; i < a.stops.length; i++) {
    if ((a.stops[i] || '').toUpperCase() !== (b.stops[i] || '').toUpperCase())
      return false;
  }
  return true;
};

const rebuildGradientWithStops = (obj, property, applyStops) => {
  const grad = obj.get(property);
  if (!grad || !grad.colorStops) return false;
  const base = grad; // use loaded gradient coords/units/transform
  const newStops = base.colorStops.map((cs, i) => ({
    offset: clamp(
      applyStops[i]?.offset ??
        cs.offset ??
        i / Math.max(1, base.colorStops.length - 1),
      0,
      1
    ),
    color: applyStops[i]?.color ?? cs.color,
    opacity: applyStops[i]?.opacity ?? cs.opacity,
    _magicStopId: cs._magicStopId ?? `stop-${i}`,
  }));
  const newGrad = new fabric.Gradient({
    type: base.type || 'linear',
    gradientUnits: base.gradientUnits,
    gradientTransform: base.gradientTransform,
    coords: base.coords,
    colorStops: newStops,
  });
  // carry original snapshot forward if present
  newGrad._original = base._original || null;
  obj.set(property, newGrad);
  obj.dirty = true;
  return true;
};

const applyOverridesToGroup = (group, overrides = []) => {
  if (!group || !overrides || overrides.length === 0) return;
  traverse(group, (o) => {
    ['fill', 'stroke'].forEach((prop) => {
      const grad = o.get && o.get(prop);
      if (!grad || !grad.colorStops) return;
      const sig = gradientSignature(grad);
      const ov = overrides.find(
        (x) => x.property === prop && signaturesEqual(sig, x.match)
      );
      if (!ov) return;
      rebuildGradientWithStops(o, prop, ov.apply?.stops || []);
    });
  });
};

export const applyMagicFrameSvgOverrides = (magicFrame) => {
  if (!magicFrame) return;
  const giotto = magicFrame.get('giotto') || {};
  const overrides = giotto.magicFrameSvgOverrides || {};
  if (magicFrame.backgroundSVG && Array.isArray(overrides.background)) {
    applyOverridesToGroup(magicFrame.backgroundSVG, overrides.background);
  }
  if (magicFrame.frameSVG && Array.isArray(overrides.frame)) {
    applyOverridesToGroup(magicFrame.frameSVG, overrides.frame);
  }
  if (magicFrame.canvas) magicFrame.canvas.renderAll();
};

export default applyMagicFrameSvgOverrides;
